import React from 'react';
import { Col, Container, Nav, Row, Tab } from 'react-bootstrap';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import UsersTab from './UsersTab';
import ListingsTab from './ListingsTab';
import RequestsTab from './RequestsTab';
import InstitutionsTab from './InstitutionsTab';
import GuidelinesTab from './GuidelinesTab';
import ReportsTab from './ReportsTab';
import EmailPreviewsTab from './EmailPreviewsTab';
import usePageTitle from '@/shared/hooks/usePageTitle';
import { useAuth } from '@/shared/contexts/AuthContext';
import { coordinatedInstitution, hasAnyRole } from '@/shared/utils/roles';
import { institutionLabel } from '@/shared/constants/institutions';
import './AdminPage.css';

const STAFF = Object.freeze(['admin', 'coordinator']);
const ADMIN_ONLY = Object.freeze(['admin']);

/**
 * The tabs, in the order a coordinator works through them.
 *
 * The key is the address as well as the tab, so a tab can be linked to: the
 * guidelines page sends an admin straight to `/admin/guidelines`. The member
 * directory is keyed `users` after the API resource it reads, while the tab
 * says Members, which is the word the rest of the site uses for a person.
 *
 * `roles` is who sees the tab, with the admin bypass. The server refuses a
 * hidden tab's calls on its own, so this decides what a campus coordinator is
 * shown rather than what they can reach.
 */
const TABS = Object.freeze([
    { key: 'users', title: 'Members', Panel: UsersTab, roles: ADMIN_ONLY },
    { key: 'listings', title: 'Listings', Panel: ListingsTab, roles: STAFF },
    {
        key: 'requests',
        title: 'Requests',
        Panel: RequestsTab,
        roles: ADMIN_ONLY,
    },
    {
        key: 'institutions',
        title: 'Institutions',
        Panel: InstitutionsTab,
        roles: STAFF,
    },
    {
        key: 'guidelines',
        title: 'Guidelines',
        Panel: GuidelinesTab,
        roles: STAFF,
    },
    { key: 'reports', title: 'Reports', Panel: ReportsTab, roles: STAFF },
    {
        key: 'emails',
        title: 'Email previews',
        Panel: EmailPreviewsTab,
        roles: ADMIN_ONLY,
    },
]);

function AdminPage() {
    const navigate = useNavigate();
    const { userProfile } = useAuth();
    const active = useParams()['*'];
    const visible = TABS.filter((tab) => hasAnyRole(userProfile, tab.roles));
    const activeTab = visible.find((tab) => tab.key === active);
    const scoped = coordinatedInstitution(userProfile);
    const area = scoped ? 'Coordinator' : 'Admin';
    // Each tab is its own view in the history and the tab strip, so it names
    // itself there (WCAG 2.4.2) rather than every tab reading "Admin".
    usePageTitle(activeTab ? `${activeTab.title} (${area})` : area);

    // `/admin`, an address naming no tab, and a tab this role does not see
    // all open the first visible tab, so a stale or shared bookmark lands
    // somewhere usable rather than on an empty page.
    if (!activeTab) {
        return visible.length > 0 ? (
            <Navigate to={`/admin/${visible[0].key}`} replace />
        ) : null;
    }

    return (
        <Container fluid className="py-4 afe-admin-container">
            <h1 className="h3 mb-1">{area}</h1>
            <p className="text-muted mb-4">
                {scoped
                    ? `Settings and listings for ${institutionLabel(scoped)}, and the exchange-wide reports.`
                    : 'Every institution, listing, request, and member in the exchange.'}
            </p>
            {/* A vertical menu beside the panel on wide screens, a scrolling
                row above it on narrow ones. It keeps the tab roles, so the
                keyboard and screen reader behaviour is the tab pattern's.
                mountOnEnter and unmountOnExit keep the panel being read the
                only one fetching: each loads on arrival rather than every
                panel loading when the page opens. */}
            <Tab.Container
                id="admin-tabs"
                activeKey={active}
                onSelect={(key) => navigate(`/admin/${key}`)}
                mountOnEnter
                unmountOnExit
            >
                <Row className="g-4">
                    <Col lg={3} xl={2}>
                        <Nav
                            variant="pills"
                            className="afe-admin-nav flex-lg-column"
                            aria-label={`${area} sections`}
                        >
                            {visible.map(({ key, title }) => (
                                <Nav.Item key={key}>
                                    <Nav.Link eventKey={key}>{title}</Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    </Col>
                    <Col lg={9} xl={10} className="afe-admin-panel">
                        <Tab.Content>
                            {visible.map(({ key, Panel }) => (
                                <Tab.Pane key={key} eventKey={key}>
                                    <Panel />
                                </Tab.Pane>
                            ))}
                        </Tab.Content>
                    </Col>
                </Row>
            </Tab.Container>
        </Container>
    );
}

export default AdminPage;
